import React, { useState, useEffect } from 'react';
import { Settings, Power, Edit3, Plus, Search, Activity, ChevronDown, ChevronRight } from "lucide-react"; 
import { UserAvatar } from "../App";

export default function Sidebar({ 
  ctx, navGroups, activeSection, setActiveSection, 
  activeProjectId, setActiveProjectId, visibleProjects, 
  unreadChat, setMobileMenuOpen, mobileMenuOpen 
}) {
  const { T, currentUser, isAdmin, logout, setCmdOpen, openCreateProject, openEditProject, api, isEN, teams, setModal, setProjectForm } = ctx;

  const isTeamLeader = teams?.some(t => t.leader_id === currentUser?.id);
  const canCreateProject = isAdmin || isTeamLeader || currentUser?.permissions?.can_create_projects !== false;

  const [hasNewIdeas, setHasNewIdeas] = useState(false);
  // Speichert, welche Gruppen eingeklappt sind
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    let interval;
    const checkIdeas = async () => {
      if (!api) return;
      try {
        const ideas = await api('/ideas');
        if (ideas && ideas.length > 0) {
          const latestIdeaId = ideas[0].id; 
          if (activeSection === "polls") {
            localStorage.setItem("tf_last_seen_idea_id", latestIdeaId.toString());
            setHasNewIdeas(false);
          } else {
            const lastSeenId = localStorage.getItem("tf_last_seen_idea_id");
            if (!lastSeenId || latestIdeaId > Number(lastSeenId)) {
              setHasNewIdeas(true);
            }
          }
        }
      } catch (e) {}
    };
    checkIdeas();
    if (activeSection !== "polls") interval = setInterval(checkIdeas, 30000);
    return () => { if (interval) clearInterval(interval); };
  }, [activeSection, api]);

  const handleNavClick = (id) => {
    setActiveSection(id);
    setMobileMenuOpen(false);
    if (id === "nl2" && ctx.loadNL2) ctx.loadNL2();
    if (id === "myTasks" && ctx.loadAllTasks) ctx.loadAllTasks();
    if (id === "reporting" && ctx.loadReporting) ctx.loadReporting();
    if (id === "admin") {
      if (ctx.loadAudit) ctx.loadAudit();
      if (ctx.loadBackups) ctx.loadBackups();
      if (ctx.loadFeedback) ctx.loadFeedback();
    }
  };

  // Funktion zum Einklappen
  const toggleGroup = (title) => {
    setCollapsedGroups(prev => ({ ...prev, [title]: !prev[title] }));
  };
  
  return (
    <div className={`sidebar ${mobileMenuOpen ? "open" : ""}`} style={{ width: 260, minWidth: 260, flexShrink: 0, display: "flex", flexDirection: "column", background: T.sidebar, borderRight: `1px solid ${T.border}`, zIndex: 1000 }}>
      
      <div className="desktop-only" style={{ height: "70px", minHeight: "70px", boxSizing: "border-box", padding: "0 24px", display: "flex", alignItems: "center", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <img 
          src={process.env.PUBLIC_URL + "/TaskFlow(no Background).jpg"} 
          alt="TaskFlow Logo" 
          style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }} 
        />
      </div>

      <div style={{ padding: "20px 16px 12px", flex: 1, overflowY: "auto" }}>
        
        {/* Die Suche direkt hier im Menü */}
        <div onClick={() => { setCmdOpen(true); setMobileMenuOpen(false); }} className="hov" style={{ display: "flex", alignItems: "center", gap: 10, background: T.input, border: `1px solid ${T.border2}`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", color: T.textMuted, fontSize: 13, marginBottom: 24 }}>
          <Search size={16} />
          <span style={{ flex: 1 }}>{isEN ? "Search..." : "Suchen..."}</span>
          <span style={{ fontSize: 10, background: T.cardHover, border: `1px solid ${T.border3}`, padding: "2px 6px", borderRadius: 6, fontWeight: 700 }}>Strg K</span>
        </div>

        {/* Zonen generieren */}
        {navGroups.map((group, gIdx) => {
          const isCollapsed = collapsedGroups[group.title];
          return (
            <div key={gIdx} style={{ marginBottom: 24 }}>
              <div 
                onClick={() => toggleGroup(group.title)} 
                className="hov" 
                style={{ fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", padding: "0 12px 8px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                {group.title}
                {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </div>
              
              {!isCollapsed && group.items.map(item => {
                const isActive = activeSection === item.id;
                const IconComp = item.icon;
                return (
                  <div 
                    key={item.id} onClick={() => handleNavClick(item.id)}
                    className="tr"
                    style={{
                      display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, cursor: "pointer", marginBottom: 2,
                      background: isActive ? T.cardHover : "transparent",
                      color: isActive ? item.color : T.textMuted,
                      fontWeight: isActive ? 700 : 500, transition: "all 0.2s ease"
                    }}
                  >
                    <IconComp size={18} strokeWidth={isActive ? 2.5 : 2} color={isActive ? item.color : T.textFaint} />
                    <span style={{ flex: 1 }}>{item.label}</span>
                    
                    {item.id === "polls" && hasNewIdeas && (
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#FF4757", boxShadow: "0 0 8px #FF4757" }} title="Neue Ideen!" />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Projekte Zone */}
        {visibleProjects.length > 0 && (
          <>
            <div style={{ padding: "0 12px 8px", fontSize: 10, fontWeight: 700, color: T.textFaint, letterSpacing: "0.12em", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
              <span onClick={() => toggleGroup('PROJEKTE')} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }} className="hov">
                {isEN ? "PROJECTS" : "PROJEKTE"} {collapsedGroups['PROJEKTE'] ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
              </span>
              
              {canCreateProject && (
                <div 
                  onClick={(e) => { e.stopPropagation(); setProjectForm({name:"",icon:"🌐",color:"#0BACBD"}); setModal("project"); }} 
                  className="hov" 
                  style={{width: 16, height: 16, borderRadius: 4, background: "rgba(14,207,217,0.15)", color: "#0ECFD9", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 14}}
                  title={isEN ? "New Project" : "Neues Projekt"}
                >
                  +
                </div>
              )}
            </div>        
            
            {!collapsedGroups['PROJEKTE'] && visibleProjects.map(p => {
              const isActive = activeProjectId === p.id && activeSection === "tasks";
              return (
                <div 
                  key={p.id} 
                  className="tr"
                  onClick={() => { setActiveProjectId(p.id); setActiveSection("tasks"); setMobileMenuOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 10, cursor: "pointer", marginBottom: 2,
                    background: isActive ? T.cardHover : "transparent", color: isActive ? T.text : T.textMuted,
                    fontWeight: isActive ? 600 : 500, transition: "all 0.2s ease", position: "relative"
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: isActive ? p.color : T.textFaint, flexShrink: 0, transition: "background 0.2s" }} />
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                  
                  {(isAdmin || p.created_by === currentUser?.id) && (
                    <div 
                      className="del" 
                      onClick={(e) => { e.stopPropagation(); openEditProject(p, e); }}
                      style={{ width: 22, height: 22, borderRadius: 6, background: T.border2, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}
                    >
                      <Edit3 size={12} />
                    </div>
                  )}

                  {unreadChat[p.id] > 0 && <span style={{ fontSize: 10, background: "#0ECFD9", color: "#0A0D15", borderRadius: 10, padding: "2px 6px", fontWeight: 700 }}>{unreadChat[p.id]}</span>}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* User Section am Boden */}
      <div style={{padding:"20px 24px", borderTop:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:12, background: T.sidebar }}>
        <UserAvatar user={currentUser} size={36} />
        <div style={{flex:1, overflow:"hidden"}}>
          <div style={{fontSize:13, fontWeight:700, color:T.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
            {currentUser?.name}
          </div>
          <div style={{fontSize:10, color:T.textGhost, fontWeight:600}}>
            {currentUser?.role?.toUpperCase()}
          </div>
        </div>
        <div style={{display: "flex", gap: 10}}>
          <button onClick={() => { setActiveSection("platform"); setMobileMenuOpen(false); }} style={{background:"none", border:"none", color:T.textMuted, cursor:"pointer", padding:0}} className="hov" title="TaskFlow Status & Feedback">
            <Activity size={18}/>
          </button>
          <button onClick={()=>setModal("settings")} style={{background:"none", border:"none", color:T.textMuted, cursor:"pointer", padding:0}} className="hov">
            <Settings size={18}/>
          </button>
          <div onClick={logout} className="hov" style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
            <Power size={18} color="#FF4757" />
          </div>
        </div>
      </div>
    </div>
  );
}